/**
 * Department Hooks
 *
 * TanStack Query hooks for department management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  Department,
  DepartmentListResponse,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
} from '../types/department';

/** Query keys for departments */
export const departmentKeys = {
  all: ['departments'] as const,
  list: () => [...departmentKeys.all, 'list'] as const,
  detail: (id: string) => [...departmentKeys.all, 'detail', id] as const,
};

/**
 * Fetch all departments
 */
export function useDepartments() {
  return useQuery({
    queryKey: departmentKeys.list(),
    queryFn: () => apiFetch<DepartmentListResponse>('/api/departments'),
    staleTime: 60_000, // 1 minute - departments change rarely
  });
}

/**
 * Create new department
 */
export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDepartmentRequest) =>
      apiFetch<Department>('/api/departments', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
    },
  });
}

/**
 * Update existing department
 */
export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateDepartmentRequest & { id: string }) =>
      apiFetch<Department>(`/api/departments/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
    },
  });
}

/**
 * Delete department
 */
export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/departments/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
    },
  });
}
